import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import isEqual from 'lodash/isEqual';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FlowsSankeyResponse} from 'common/report/funnels/models/api-types';
import {SankeyChartEl} from 'common/widgets/sankey-chart';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SankeyFlow} from 'common/widgets/sankey-chart/models/sankey-flow';
import {SankeyNode} from 'common/widgets/sankey-chart/models/sankey-node';
import {FlowsBookmark} from 'common/report/flows/models/types';
import {FlowsReport} from 'common/report/flows/models/report';
import {getDownloadMode} from 'common/report/util';
import {DownloadMode} from 'common/report/constants';
import {NodeMenuEvent} from 'common/widgets/sankey-chart/models/types';
import {ContextMenuEl} from 'common/widgets/context-menu';
import {SectionItem} from 'common/components/items-menu/types';
import {isOtherSegment} from 'common/query-builder/query-block/segment-menu/types';
import {ParamsAndResults} from 'common/report/types';
import {SankeyBreakdown} from 'common/widgets/sankey-chart/models/sanky-breakdown';

import css from './index.inline.styl';

interface FlowsSankeyChartAttrs {
  'scroll-offset': number;
  loaded: boolean;
  'tooltip-enabled': boolean;
  'disable-add-steps': boolean;
  'disable-click-node': boolean;
}

type NodePopoverCallback = (node: SankeyNode) => {options: SectionItem[]; breakdownOptions?: UnknownObj[]};

export interface FlowsSankeyChartJsxProps {
  attrs: FlowsSankeyChartAttrs;
  props: {
    nodePopoverCallback: NodePopoverCallback;
    paramsAndResults: ParamsAndResults<FlowsBookmark, FlowsSankeyResponse>;
  };
}

interface FlowsSankeyChartState {
  report: FlowsReport | null;
  sankeyFlow: SankeyFlow | null;
  node: SankeyNode;
  popoverBreakdown: SankeyBreakdown;
  popoverStyles: {top: string; left: string};
}

export class FlowsSankeyChart extends Component<
  FlowsSankeyChartState,
  FlowsSankeyChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  private _flowsResponse: FlowsSankeyResponse;
  private _bookmarkParams: FlowsBookmark;
  private _nodePopoverCallback: NodePopoverCallback;

  private contextMenuKey = 0;

  static get attrsSchema(): AttrsSchema<FlowsSankeyChartAttrs> {
    return {
      ...super.attrsSchema,
      'scroll-offset': `number`,
      loaded: `boolean`,
      'tooltip-enabled': `boolean`,
      'disable-add-steps': `boolean`,
      'disable-click-node': `boolean`,
    };
  }

  connectedCallback() {
    this.processData();
    super.connectedCallback();
  }

  get config(): ConfigOptions<FlowsSankeyChartState, any, ChartsContextRegistry> {
    return {
      useShadowDom: true,
      defaultState: {
        report: null,
        sankeyFlow: null,
        node: null,
        popoverBreakdown: null,
        popoverStyles: null,
      },
      contexts: [`urlManager` as const, `currentProject` as const],
      css,
      template: () => this.renderTemplate(),
    };
  }

  public set paramsAndResults({params, results}: ParamsAndResults<FlowsBookmark, FlowsSankeyResponse>) {
    if (!isEqual(this._bookmarkParams, params) || !isEqual(this._flowsResponse, results)) {
      this._flowsResponse = results;
      this._bookmarkParams = params;
      this.processData();
    }
  }

  public set nodePopoverCallback(cb: NodePopoverCallback) {
    this._nodePopoverCallback = cb;
  }

  private processData(): void {
    if (!(this._flowsResponse && this._bookmarkParams && this.isConnected)) {
      return;
    }
    const report = FlowsReport.fromBookmarkParams(this._bookmarkParams);
    const queryResult = {
      ...this._flowsResponse,
      ...(Object.prototype.hasOwnProperty.call(this._flowsResponse, `results`) ? this._flowsResponse[`results`] : {}),
    };
    if (!queryResult[`steps`]) {
      queryResult[`steps`] = [];
    }
    const sankeyFlow: SankeyFlow = new SankeyFlow(queryResult, report.getEventsAsFlowQueryEntries(), report.alignment);
    this.update({report, sankeyFlow});
  }

  private getStepRenameMap(): string[] {
    return this.state.report.events.map((step: {event: {renamed?: string; label: string}}) => {
      return step.event?.renamed || step.event?.label;
    });
  }

  private canDecreaseCardinality(): boolean {
    return this.state.report.cardinality >= 2;
  }

  private isDownloadMode(): boolean {
    return getDownloadMode(this.getContext(`urlManager`)) !== DownloadMode.None;
  }

  private onNodeClick(ev: NodeMenuEvent) {
    if (!this._nodePopoverCallback) {
      return;
    }

    const {node, breakdown, pageX, pageY} = ev.detail;

    const parentRect = this.offsetParent.getBoundingClientRect();
    const gap = 12;
    const left = pageX - parentRect.x - window.scrollX + gap;
    const top = pageY - parentRect.y - window.scrollY + gap;

    this.update({
      node,
      popoverBreakdown: breakdown,
      popoverStyles: {left: `${left}px`, top: `${top}px`},
    });
  }

  private handlePopoverAction(ev) {
    this.dispatchEvent(
      new CustomEvent(`nodePopoverAction`, {
        detail: {
          action: ev.detail.action,
          node: this.state.node,
          breakdown: this.state.popoverBreakdown,
          value: ev.detail.value,
        },
      }),
    );
  }

  private handlePopoverClose() {
    this.update({node: null, popoverStyles: null});
  }

  private renderPopover() {
    if (!this.state.node || !this._nodePopoverCallback) {
      return <></>;
    }

    const {options, breakdownOptions} = this._nodePopoverCallback(this.state.node);
    return (
      <ContextMenuEl
        attrs={{
          'breakdown-options': breakdownOptions,
          'menu-items': options,
          'breakdown-event': this.state.node.event,
          'relative-positioning': true,
          open: true,
          position: {left: 0, top: 0},
          'project-id': this.getContext(`currentProject`).get().value?.id,
        }}
        style={this.state.popoverStyles}
        key={this.contextMenuKey++}
        on={{
          action: (ev: CustomEvent) => this.handlePopoverAction(ev),
          close: () => this.handlePopoverClose(),
        }}
      />
    );
  }

  private renderTemplate() {
    return (
      <div sel='.sankey-widget'>
        <SankeyChartEl
          class={{
            loaded: !!this.attr(`loaded`),
          }}
          attrs={{
            alignment: JSON.stringify(this.state.report.alignment),
            alignbreakdowns: this.state.report.alignBreakdowns,
            alwaysbreakdown: this.state.report.alwaysBreakdown,
            'anchor-label-overrides': JSON.stringify(this.getStepRenameMap()),
            reverse: false,
            'can-decrease-cardinality': this.canDecreaseCardinality(),
            'tooltip-enabled': this.attr(`tooltip-enabled`) && !this.state.node,
            'disable-add-step': this.attr(`disable-add-steps`) || this.isDownloadMode(),
            'disable-click-node': this.attr(`disable-click-node`),
            'disable-lift': !(
              this.state.report.events.length > 1 &&
              this.state.report.groupBy.filter((s) => isOtherSegment(s)).length > 0
            ),
            'scroll-offset': this.attr(`scroll-offset`),
            'count-type': this.state.report.countType,
          }}
          props={{
            data: this.state.sankeyFlow,
          }}
          on={{
            updateSteps: (ev: CustomEvent<{operation; type; anchor; absolute; scrollOffset}>) => {
              this.dispatchEvent(new CustomEvent(`updateSteps`, ev));
            },
            nodeClick: (ev: NodeMenuEvent) => this.onNodeClick(ev),
          }}
        />
        {this.renderPopover()}
      </div>
    );
  }
}

export const FlowsSankeyChartWidgetEl = defineAndMakeJsxFactory(`mp-flows-sankey-chart-widget`, FlowsSankeyChart);
export const FlowsSankeyChartWidgetReact = panel2react(`mp-flows-sankey-chart-widget`, FlowsSankeyChart);
